import Big from 'big.js';
import type {BigSource} from 'big.js';
import {QtyType} from '@px/enum/order';
import {numUtil} from './num';

/**
 *  委托单位相关的转换
 *  转换数量和单位
 *  在展示合约数量的时候使用
 *
 *  新/老区别：
 *    V2之前、都以 cont 为单位下单
 *    V2之后、都以 contractUnderlyingAssets 为单位下单
 */
export const qtyUtil = {
  toValueUnit(contract: ContractPair): string {
    if (isV2(contract)) {
      return contract.settleCurrencyDisplay;
    }
    if (contract.contractSide > 0) {
      return contract.settleCurrencyDisplay;
    }
    return contract.contractUnderlyingAssets;
  },
  toValueQuantity(contract: ContractPair, size: BigSource, price: BigSource): number | '-' {
    if (isV2(contract)) {
      if (invalidPrice(price)) {
        return '-';
      }
      // USDT 等稳定币
      return Number(numUtil.floor(Big(size).mul(price), 4));
    }

    if (contract.contractSide > 0) {
      // 多少 usd,  1张 * contractSize(1张多少btc) * price
      if (invalidPrice(price)) {
        return '-';
      }
      return Number(numUtil.floor(Big(size).mul(contract.contractSize).mul(price), 2));
    }
    // 多少 usd,  1张 * contractSize(1张多少usd)
    return Number(numUtil.floor(Big(size).mul(contract.contractSize), 2));
  },
  toValue(contract: ContractPair, size: BigSource, price: BigSource): string {
    return display(qtyUtil.toValueQuantity(contract, size, price), qtyUtil.toValueUnit(contract));
  },

  toCoinUnit(contract: ContractPair): string {
    if (isV2(contract)) {
      return contract.contractUnderlyingAssets;
    }

    if (contract.contractSide > 0) {
      return contract.contractUnderlyingAssets;
    }
    return contract.settleCurrency;
  },
  toCoinQuantity(contract: ContractPair, size: BigSource, price: BigSource): number | '-' {
    if (isV2(contract)) {
      return Number(numUtil.floor(size, contract.qtyPrecision));
    }

    if (contract.contractSide > 0) {
      // 多少 btc, size 张 * contractSize(1张多少btc)
      return Number(numUtil.floor(Big(size).mul(contract.contractSize), 4));
    }

    // 多少 btc, size 张 * contractSize(1张多少usd) / price
    // 价格为 0 的 case 举例：
    // 以市价 + reduceOnly 下的 order，仓位被其他操作平了，这个 order 就会没有任何价格
    if (invalidPrice(price) || Number(price) === 0) {
      return '-';
    }
    return Number(numUtil.floor(Big(size).mul(contract.contractSize).div(price), 8));
  },
  toCoin(contract: ContractPair, size: BigSource, price: BigSource): string {
    return display(qtyUtil.toCoinQuantity(contract, size, price), qtyUtil.toCoinUnit(contract));
  },

  toContUnit(contract: ContractPair): string {
    return isV2(contract) ? '' : 'Cont';
  },
  toContQuantity(contract: ContractPair, size: BigSource): number | '-' {
    if (isV2(contract)) {
      return '-';
    }
    return Number(size);
  },
  toCont(contract: ContractPair, size: BigSource): string {
    return display(qtyUtil.toContQuantity(contract, size), qtyUtil.toContUnit(contract));
  },
  getUnit(contract: ContractPair, qtyType: QtyType) {
    const map = {
      [QtyType.Cont]: () => qtyUtil.toContUnit(contract),
      [QtyType.Base]: () => qtyUtil.toValueUnit(contract),
      [QtyType.Coin]: () => qtyUtil.toCoinUnit(contract),
    };
    return map[qtyType]();
  },
  convert(contract: ContractPair, qtyType: QtyType, size: BigSource, price: BigSource) {
    const map = {
      [QtyType.Cont]: () => qtyUtil.toContQuantity(contract, size),
      [QtyType.Base]: () => qtyUtil.toValueQuantity(contract, size, price),
      [QtyType.Coin]: () => qtyUtil.toCoinQuantity(contract, size, price),
    };
    return map[qtyType]();
  },
};

export function getQtyTypePriceByOrderType(item, type: OrderType): BigSource {
  // 市价 条件单/止损单 (MarketIfTouched/Stop) 使用触发价
  // 限价 条件单/止损单 (LimitIfTouched/StopLimit) 依旧使用 priceEp
  // 其他使用 priceEp
  const map = {
    MarketIfTouched: item.stopPxEp,
    Stop: item.stopPxEp,
  };
  return map[type] || item.priceEp;
}

function isV2(contract: ContractPair): boolean {
  return contract.type === 'PerpetualV2';
}

function display(size: BigSource, unit: string): string {
  size = size === undefined ? '-' : size;
  return unit ? `${numUtil.delimit(size)} ${unit}` : `${size}`;
}

function invalidPrice(price: any): boolean {
  return isNaN(price) || price === '';
}
